"use client";

import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { getPassData, getSubscriptionData } from "./subscription-service";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SubscriptionPackages from "./subscriptionPackages";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { DEVICE_RESOLUTION } from "../../app-constants";
import { appendSourceParameter, sendEvent } from "../../../gtm/functions";
import { IGTMEventType } from "../../../gtm/IGTMEventType";
import Notes from "./components/Notes";
import SeoSection from "./components/SeoSection";
import Winners from "./components/Winners";
import Sticker from "./components/Sticker";
import Specs from "./components/Specs";
import StickyScrollButton from "./components/StickyScrollButton";
import ExclusiveDraws from "@/app/(general-header)/botb-pass/components/ExclusiveDraws";
import DeviceProvider from "@/app/redux/features/device/DeviceProvider";

const PassPage = ({
  headerTextClassName,
  isUpsellModal,
  updateMilestones,
}: {
  headerTextClassName?: string;
  isUpsellModal?: boolean;
  updateMilestones?: Dispatch<SetStateAction<string>>;
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(1);
  const [data, setData] = useState<BotBPassProps>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [windowInnerWidth, setWindowInnerWidth] = useState<number>(window.innerWidth);
  const [top, setUmbracoContentTop] = useState<string>();
  const ref = useRef<HTMLDivElement>();
  const mainHeaderRef = useRef<HTMLDivElement>();
  const [mainHeaderTop, setMainHeaderTop] = useState<string>();
  const [headerSectionMinHeight, setHeaderSectionMinHeight] = useState<string>();
  const bannerTextRef = useRef<HTMLDivElement>();

  const [isMobile, setIsMobile] = useState(
    window.innerWidth >= DEVICE_RESOLUTION.MobileMin && window.innerWidth < DEVICE_RESOLUTION.Laptop
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth >= DEVICE_RESOLUTION.MobileMin && window.innerWidth < DEVICE_RESOLUTION.Laptop);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    sendEvent(
      isUpsellModal ? IGTMEventType.SubscriptionUpsellPopupView : IGTMEventType.SubscriptionPageView,
      appendSourceParameter(isUpsellModal)
    );
    (async () => {
      try {
        const response = await getPassData();
        const data = (await response.json()) as BotBPassProps;
        setData(data);
        updateMilestones(data.Milestones);
      } catch (err) {
        console.error(`Error while retreiving subscriptions - ${JSON.stringify(err)}`);
      } finally {
        setIsLoading(false);
      }
    })();
    renderRespectiveLayout();
    window.addEventListener("resize", renderRespectiveLayout);
    return () => {
      window.removeEventListener("resize", renderRespectiveLayout);
    };
  }, []);

  useEffect(() => {
    let activatePassButton: Element;
    if (bannerTextRef.current) {
      activatePassButton = bannerTextRef.current.querySelector(".activate-pass-button");
      if (activatePassButton) {
        activatePassButton.addEventListener("click", onActivatePass);
      }
    }
    return () => {
      window.removeEventListener("click", onActivatePass);
    };
  }, [data, windowInnerWidth, isMobile]);

  useEffect(() => {
    if (ref.current) {
      if (windowInnerWidth >= DEVICE_RESOLUTION.Desktop) {
        const refTop = ref.current?.getBoundingClientRect().height / 2;
        const headerTop = ref.current?.getBoundingClientRect().height / 2 + 48;
        const sectionMinHeight =
          mainHeaderRef.current?.getBoundingClientRect().height + ref.current?.getBoundingClientRect().height / 2;
        setUmbracoContentTop(`-${refTop}px`);
        setMainHeaderTop(`${headerTop}px`);
        setHeaderSectionMinHeight(`${sectionMinHeight + 48}px`);
      } else {
        const refTop = ref.current?.getBoundingClientRect().height;
        const headerTop =
          ref.current?.getBoundingClientRect().height - ref.current?.getBoundingClientRect().height / 20 + 24;
        const setionHeight =
          ref.current?.getBoundingClientRect().height -
          ref.current?.getBoundingClientRect().height / 20 +
          24 +
          mainHeaderRef.current?.getBoundingClientRect().height;
        setUmbracoContentTop(`-${refTop / 20}px`);
        setMainHeaderTop(`${headerTop}px`);
        setHeaderSectionMinHeight(`${setionHeight}px`);
      }
    }
  }, [ref.current, windowInnerWidth, mainHeaderRef.current, data]);

  const renderRespectiveLayout = () => {
    setWindowInnerWidth(window.innerWidth);
  };

  useEffect(() => {
    if (!isLoading && window.location.hash === "#main-plans-section") {
      const el = document.getElementById("main-plans-section");
      if (!el) return;

      let header = document.querySelector(".botb-pass-header") as HTMLElement | null;
      let headerHeight = header?.offsetHeight || 0;
      let attempts = 0;

      const scrollToSection = () => {
        const elTop = el.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
        window.scrollTo({
          top: elTop,
          behavior: "smooth",
        });
      };

      const tryScroll = () => {
        headerHeight = header?.offsetHeight || 0;

        scrollToSection();

        attempts++;
        if (attempts < 5) {
          // retry 5 times for layout shift
          setTimeout(tryScroll, 200);
        }
      };

      const timeout = setTimeout(tryScroll, 300);

      return () => clearTimeout(timeout);
    }
  }, [isLoading, mainHeaderTop, headerSectionMinHeight]);

  const getPostalEntryTextContent = () =>
    isLoading ? (
      <Skeleton
        count={1}
        inline
        highlightColor="#fff"
        containerClassName="flex flex-col w-full px-12 pb-5 h-[500px]"
        className="h-full"
      />
    ) : (
      <div className="bg-[#E7E7E9] lg:pb-20">
        <div
          dangerouslySetInnerHTML={{
            __html: data?.PostalEntryText,
          }}
          className="rounded-lg m-auto px-4 py-6 lg:bg-white lg:mt-8 lg:p-8 max-w-[664px] [&>h2]:normal-case [&>h2]:text-secondary [&>h2]:text-left [&>h2]:text-2xl [&>h2]:leading-tight [&>h2]:mb-3 [&>h2]:font-medium [&>p]:mb-6 [&>p]:text-gray-1 [&>p]:text-sm [&>p]:tracking-normal [&>p:last-of-type]:mb-0 [&>ul]:mb-6 [&>ul>li]:text-gray-1 [&>ul>li]:text-sm [&>ul>li]:tracking-normal"
        />
      </div>
    );

  const onActivatePass = () => {
    const headerNode = document.querySelector(".botb-pass-header");
    let headerHeight = 0;
    if (headerNode) {
      headerHeight = headerNode.getBoundingClientRect().height;
    }
    window.scrollTo({
      top: mainHeaderRef.current?.getBoundingClientRect().top - headerHeight,
      behavior: "smooth",
    });
  };

  const [subscriptionDataLoading, setSubscriptionDataLoading] = React.useState<boolean>(false);
  const [subscriptionData, setSubscriptionData] = useState<PreSubscriptionData>({
    features: [],
    header: {
      subtitlep1: "",
      subtitlep2: "",
      subtitlep3: "",
      title: "",
      video: "",
      videoMobile: "",
      legalText: "",
    },
    offer: {
      bonus: "",
      description: "",
      expireTime: 0,
      headline: "",
      offerImage: "",
      offerTitle: "",
    },
  });

  useEffect(() => {
    setSubscriptionDataLoading(true);
    getSubscriptionData(false, "default")
      .then(res => res.json())
      .then(res => {
        setSubscriptionData(res);
        setSubscriptionDataLoading(false);
      })
      .catch(err => console.error(err));
  }, []);

  const ButtonTopBanner = () => {
    return (
      <button className="theme-btn theme-btn-primary theme-btn-md lg:theme-btn-2xl uppercase activate-pass-button">
        View plans{" "}
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M4.66797 4.66699L8.0013 8.00033L4.66797 11.3337M8.66797 4.66699L12.0013 8.00033L8.66797 11.3337"
            stroke="white"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"></path>
        </svg>
      </button>
    );
  };

  return (
    <DeviceProvider>
      <div className="w-full bg-[#E7E7E9]">
        <>
          {isLoading && !isUpsellModal && (
            <Skeleton
              count={1}
              inline
              highlightColor="#fff"
              containerClassName="flex flex-col w-full"
              className="!h-[441px] lg:!h-[400px]"
            />
          )}
          {!isLoading && !isUpsellModal && (
            <Swiper
              slidesPerView={1}
              loop={false}
              spaceBetween={0}
              className="w-full"
              style={{
                zIndex: 0,
              }}>
              {data?.BannerList?.map((banner, index) => (
                <>
                  <SwiperSlide key={`banner_top_${index}`}>
                    <div className={"relative !min-h-[441px] lg:!min-h-[400px] size-full flex items-center px-4 py-8"}>
                      <picture>
                        {banner.DesktopImage !== "" && (
                          <source srcSet={banner.DesktopImage} media="(min-width: 1024px)" />
                        )}
                        {banner.MobileImage !== "" && <source srcSet={banner.MobileImage} />}
                        <img
                          src={banner.MobileImage}
                          alt="BotB-pass-banner-image"
                          fetchPriority="high"
                          className="absolute inset-0 size-full object-cover"
                        />
                      </picture>

                      <div
                        ref={bannerTextRef}
                        className={
                          "size-full relative z-[1] max-w-[1142px] mx-auto flex max-lg:flex-col items-center gap-8 lg:gap-12 lg:h-auto lg:items-end"
                        }>
                        <div className="flex flex-col w-full max-lg:text-center max-lg:items-center lg:items-start max-w-[600px] gap-8">
                          <div dangerouslySetInnerHTML={{ __html: banner.Html }} />
                          {!isMobile && ButtonTopBanner()}
                        </div>
                        <div className={"flex flex-col items-center gap-8 lg:gap-[60px] max-lg:mt-auto"}>
                          <div>
                            <Sticker
                              subscriptionData={subscriptionData}
                              subscriptionDataLoading={subscriptionDataLoading}
                            />
                          </div>
                          {isMobile && ButtonTopBanner()}
                          {!isUpsellModal && (
                            <div ref={ref} className="" dangerouslySetInnerHTML={{ __html: data?.BiggerMilestones }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                </>
              ))}
            </Swiper>
          )}
        </>
        <div className="flex flex-col items-center bg-white relative">
          <div
            id={"main-plans-section"}
            className="w-full flex flex-col gap-4 lg:gap-6 items-center relative"
            ref={mainHeaderRef}>
            <div className={"text-center px-3 mt-4 lg:mt-8"}>
              <div
                className={`w-[336px] text-primary text-center font-jost text-xl font-medium leading-[26px] lg:text-[28px] lg:font-semibold lg:leading-8`}>
                Choose how to play:
              </div>
            </div>
            <div className="flex xs:w-full lg:w-auto">
              <div
                className={`w-1/2 lg:w-[210px] px-2 cursor-pointer items-center justify-center flex flex-col border-b-1.5 lg:border-b-2 border-solid ${activeTabIndex === 0 ? "border-[#FF8200]" : "border-transparent"}`}
                onClick={() => setActiveTabIndex(0)}>
                <div className={"flex flex-col items-center pb-2.5 lg:pb-4"}>
                  <span
                    className={`uppercase font-jost text-sm leading-5 font-medium lg:text-base lg:leading-[1.375rem] ${activeTabIndex === 0 ? "text-[#28282B]" : "text-[#696971]"}`}>
                    Postal
                  </span>
                  <span className="text-[#898994] font-jost text-[11px] lg:text-sm font-normal -mt-0.5">
                    No purchase necessary
                  </span>
                </div>
              </div>
              <div
                className={`w-1/2 lg:w-[210px] px-2 cursor-pointer items-center justify-center flex flex-col border-b-1.5 lg:border-b-2 border-solid ${activeTabIndex === 1 ? "border-[#FF8200]" : "border-transparent"}`}
                onClick={() => setActiveTabIndex(1)}>
                <div className={"flex flex-col items-center pb-2.5 lg:pb-4"}>
                  <span
                    className={`uppercase font-jost text-sm leading-5 font-medium lg:text-base lg:leading-[1.375rem] ${activeTabIndex === 1 ? "text-[#28282B]" : "text-[#696971]"}`}>
                    Subscription
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {activeTabIndex === 0 ? (
          getPostalEntryTextContent()
        ) : (
          <div className={"mt-6 mb-5 lg:mb-8 lg:mt-10"}>
            <SubscriptionPackages isUpsellModal={isUpsellModal} />
          </div>
        )}
        <div className="w-full flex flex-col items-center gap-6 lg:gap-11 px-4 pb-[69px] lg:pb-[97px]">
          <div className={"lg:w-full lg:max-w-[1340px] lg:mx-auto"}>
            <Notes subscriptionData={subscriptionData} />
            <ExclusiveDraws />
            <Specs />
            <Winners />
            <SeoSection />
            <StickyScrollButton sectionId="botb-cards" isLoading={isLoading} label={"View plans"} />
          </div>
        </div>
      </div>
    </DeviceProvider>
  );
};

export default PassPage;
